/**
 * RhymeResultsDisplay - Main results container for rhyme search
 *
 * Displays categorized rhyme results with recommendations panel, legend,
 * and collapsible category sections organized by syllable count.
 *
 * Scope 06: parentWordId removed. Collection is flat words —
 * the grouping engine handles relationships.
 */

import React, { useState } from 'react'
import { RhymeCategorySection } from './RhymeCategorySection'
import type { RhymeSearchResponse } from '../../types/wordTools'

// Family subcategory order (phonetic grouping)
const FAMILY_SUBCATEGORIES = [
  { key: 'voiced_plosive', label: 'Voiced Plosive' },
  { key: 'voiced_fricative', label: 'Voiced Fricative' },
  { key: 'voiced_nasal', label: 'Voiced Nasal' },
  { key: 'unvoiced_plosive', label: 'Unvoiced Plosive' },
  { key: 'unvoiced_fricative', label: 'Unvoiced Fricative' }
] as const

interface RhymeResultsDisplayProps {
  /** Search results from API */
  results: RhymeSearchResponse
  /** The word being searched */
  searchWord: string
}

/**
 * Main display component for rhyme search results.
 *
 * Features:
 * - Recommendations panel (sticky) for recommended categories
 * - Collapsible legend explaining visual language
 * - Category sections in canonical order
 * - Family rhyme subcategory handling
 * - Syllable grouping within each category
 */
export const RhymeResultsDisplay: React.FC<RhymeResultsDisplayProps> = ({
  results,
  searchWord,
}) => {
  const [legendOpen, setLegendOpen] = useState(false)

  const { categories } = results

  // Determine which categories are recommended
  const recommendedCategories = []
  if (categories.perfect.recommended) recommendedCategories.push('Perfect')
  if (categories.family.recommended) recommendedCategories.push('Family')
  if (categories.additive.recommended) recommendedCategories.push('Additive')
  if (categories.subtractive.recommended) recommendedCategories.push('Subtractive')
  if (categories.assonance.recommended) recommendedCategories.push('Assonance')
  if (categories.consonance.recommended) recommendedCategories.push('Consonance')

  const hasRecommendations = recommendedCategories.length > 0

  return (
    <div data-testid="rhyme-results-display" className="space-y-4">
      {/* Recommendations Panel - sticky at top */}
      {hasRecommendations && (
        <div
          data-testid="rhyme-recommendations-panel"
          className="sticky top-0 z-10 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3"
        >
          <div className="flex items-start gap-2">
            <span className="text-blue-700 text-lg">⭐</span>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 mb-1">
                Recommended Categories
              </h3>
              <p className="text-xs text-blue-700">
                Based on rhythmic stability analysis, these categories are likely to work well:
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {recommendedCategories.map(cat => (
                  <span
                    key={cat}
                    className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-800 rounded"
                  >
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend Toggle */}
      <div className="flex justify-end">
        <button
          data-testid="rhyme-legend-toggle"
          onClick={() => setLegendOpen(!legendOpen)}
          className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1 transition-colors"
          aria-expanded={legendOpen}
          aria-controls="rhyme-legend-panel"
        >
          <span className="text-sm">ⓘ</span>
          <span>{legendOpen ? 'Hide' : 'Show'} Visual Legend</span>
        </button>
      </div>

      {/* Legend Panel */}
      {legendOpen && (
        <div
          id="rhyme-legend-panel"
          data-testid="rhyme-legend-panel"
          className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs space-y-3"
        >
          <h4 className="font-semibold text-gray-900 text-sm">Visual Language Guide</h4>

          <div className="space-y-2">
            <div>
              <span className="font-medium text-gray-700">Category Colors:</span>
              <ul className="list-disc list-inside text-gray-600 ml-2 mt-1 space-y-1">
                <li><span className="text-green-700">Green</span> - Perfect rhymes</li>
                <li><span className="text-blue-700">Blue</span> - Family rhymes</li>
                <li><span className="text-cyan-700">Cyan</span> - Additive rhymes</li>
                <li><span className="text-yellow-700">Yellow</span> - Subtractive rhymes</li>
                <li><span className="text-orange-700">Orange</span> - Assonance</li>
                <li><span className="text-purple-700">Purple</span> - Consonance</li>
              </ul>
            </div>

            <div>
              <span className="font-medium text-gray-700">Chip States:</span>
              <ul className="list-disc list-inside text-gray-600 ml-2 mt-1 space-y-1">
                <li>Outline - Not yet collected</li>
                <li>Filled with ✓ - Already in your collection</li>
              </ul>
            </div>

            <div>
              <span className="font-medium text-gray-700">Syllable Grouping:</span>
              <p className="text-gray-600 ml-2 mt-1">
                Words are organized by syllable count (1, 2, 3, 4+) within each category
                to help match your song's rhythmic pattern.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable categories */}
      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
        {/* Perfect Rhymes */}
        <RhymeCategorySection
          title="Perfect Rhymes"
          rhymeType="perfect"
          category={categories.perfect}
          description="Identical sounds from vowel onward"
          recommended={categories.perfect.recommended}
          searchWord={searchWord}
        />

        {/* Family Rhymes - with subcategories */}
        <FamilyRhymesSection
          family={categories.family}
          searchWord={searchWord}
        />

        {/* Additive Rhymes */}
        <RhymeCategorySection
          title="Additive Rhymes"
          rhymeType="additive"
          category={categories.additive}
          description="Add sound at end"
          recommended={categories.additive.recommended}
          searchWord={searchWord}
        />

        {/* Subtractive Rhymes */}
        <RhymeCategorySection
          title="Subtractive Rhymes"
          rhymeType="subtractive"
          category={categories.subtractive}
          description="Remove sound at end"
          recommended={categories.subtractive.recommended}
          searchWord={searchWord}
        />

        {/* Assonance */}
        <RhymeCategorySection
          title="Assonance"
          rhymeType="assonance"
          category={categories.assonance}
          description="Matching vowel sounds"
          recommended={categories.assonance.recommended}
          searchWord={searchWord}
        />

        {/* Consonance */}
        <RhymeCategorySection
          title="Consonance"
          rhymeType="consonance"
          category={categories.consonance}
          description="Matching consonant sounds"
          recommended={categories.consonance.recommended}
          searchWord={searchWord}
        />
      </div>
    </div>
  )
}

/**
 * Family rhymes section with subcategory handling
 */
interface FamilyRhymesSectionProps {
  family: RhymeSearchResponse['categories']['family']
  searchWord: string
}

const FamilyRhymesSection: React.FC<FamilyRhymesSectionProps> = ({
  family,
  searchWord,
}) => {
  const [isExpanded, setIsExpanded] = useState(true)

  // Calculate total count across all subcategories
  const totalCount = FAMILY_SUBCATEGORIES.reduce((sum, { key }) => {
    const subcategory = family[key]
    return (
      sum +
      subcategory.syllables_1.length +
      subcategory.syllables_2.length +
      subcategory.syllables_3.length +
      subcategory.syllables_4_plus.length
    )
  }, 0)

  if (totalCount === 0) {
    return null
  }

  return (
    <div
      data-testid="rhyme-category-family"
      className="border border-gray-200 rounded-lg p-3"
    >
      {/* Header */}
      <button
        data-testid="rhyme-category-toggle-family"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition-colors"
        title="Family Rhyme - Similar consonant patterns"
        aria-expanded={isExpanded}
        aria-controls="category-content-family"
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900">Family Rhymes</h4>
            {family.recommended && (
              <span className="text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                ⭐ Recommended
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">Shared consonants, varied vowels</p>
        </div>

        <div className="flex items-center gap-3 ml-4">
          <span className="text-xs font-medium text-gray-500">
            {totalCount} {totalCount === 1 ? 'word' : 'words'}
          </span>
          <span className="text-gray-400 text-sm">
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
      </button>

      {/* Subcategories */}
      {isExpanded && (
        <div id="category-content-family" className="mt-3 space-y-4">
          {FAMILY_SUBCATEGORIES.map(({ key, label }) => {
            const subcategoryData = family[key]
            const subcategoryCount =
              subcategoryData.syllables_1.length +
              subcategoryData.syllables_2.length +
              subcategoryData.syllables_3.length +
              subcategoryData.syllables_4_plus.length

            if (subcategoryCount === 0) return null

            return (
              <div key={key} className="pl-3 border-l-2 border-blue-200">
                <RhymeCategorySection
                  title={label}
                  rhymeType="family"
                  category={subcategoryData}
                  description={`${subcategoryCount} ${subcategoryCount === 1 ? 'word' : 'words'}`}
                  recommended={false}
                  searchWord={searchWord}
                  subcategory={key}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
